import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, CircleAlert, CircleHelp, Droplets, Filter, Loader2, Radio, RefreshCw, Thermometer, TriangleAlert, Wifi } from "lucide-react";
import { PageHeader, Panel } from "@/components/Panel";
import { getFieldMildewRisk, getFields, getFieldsWithDevices } from "@/api/treeplet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MILDEW_STATE = {
  NO_RISK: { label: "Sin riesgo", description: "Estado actual sin riesgo", icon: CheckCircle2, className: "text-stable" },
  RISK: { label: "Riesgo detectado", description: "Este dispositivo contribuye al riesgo del campo", icon: TriangleAlert, className: "text-critical" },
  UNKNOWN: { label: "Datos insuficientes", description: "No se puede confirmar un estado actual", icon: CircleHelp, className: "text-moderate" },
};

const EVALUATION_STATE = {
  CURRENT: "Evaluación actual",
  STALE: "Evaluación obsoleta",
  NEVER_EVALUATED: "Evaluación pendiente",
};

const stateLabel = (device) => {
  if (device.lifecycle_state === "assigned") return { label: "Asignado", className: "text-moderate", icon: CircleHelp };
  const state = device.connectivity?.effective_state || "unknown";
  if (state === "online") return { label: "En línea", className: "text-stable", icon: Wifi };
  if (state === "stale") return { label: "Sin datos recientes", className: "text-moderate", icon: CircleAlert };
  if (state === "offline") return { label: "Sin conexión", className: "text-critical", icon: CircleAlert };
  return { label: "Desconocido", className: "text-ink-tertiary", icon: CircleHelp };
};

const formatTime = (value) => value
  ? new Intl.DateTimeFormat("es-ES", { dateStyle: "short", timeStyle: "short" }).format(new Date(value))
  : "Sin datos";

const formatValue = (value, unit) => value == null ? "—" : `${Number(value).toFixed(1)} ${unit}`;

const deviceId = (device) => device.id || device.device_id;

const mildewDetails = (device) => device.mildew || {
  state: device.mildew_state || "UNKNOWN",
  evaluation_status: device.evaluation_status || "NEVER_EVALUATED",
  reason_codes: device.reason_codes || [],
};

const latestMeasurements = (device) => device.latest_measurements || {};

const measurementCount = (latest) => [
  latest.leaf_temperature_c,
  latest.leaf_humidity_pct,
  latest.air_temperature_c,
  latest.air_humidity_pct,
].filter((value) => value != null).length;

function MeasurementCell({ device, detailed }) {
  if (!detailed) return <span className="text-xs text-ink-tertiary">Selecciona un campo para ver valores</span>;
  const latest = latestMeasurements(device);
  const count = measurementCount(latest);
  if (!count) return <span className="text-xs text-ink-tertiary">Sin mediciones</span>;
  return (
    <div className="space-y-1 text-xs min-w-[190px]">
      <div className="flex items-center gap-2"><span className="text-ink-secondary w-12">Hoja</span><Thermometer size={13} className="text-ink-tertiary" /> {formatValue(latest.leaf_temperature_c, "°C")} <Droplets size={13} className="text-ink-tertiary" /> {formatValue(latest.leaf_humidity_pct, "%")}</div>
      <div className="flex items-center gap-2"><span className="text-ink-secondary w-12">Aire</span><Thermometer size={13} className="text-ink-tertiary" /> {formatValue(latest.air_temperature_c, "°C")} <Droplets size={13} className="text-ink-tertiary" /> {formatValue(latest.air_humidity_pct, "%")}</div>
      {count < 4 && <div className="text-moderate font-medium">Medición parcial</div>}
    </div>
  );
}

function MildewCell({ device }) {
  const mildew = mildewDetails(device);
  const state = MILDEW_STATE[mildew.state] || MILDEW_STATE.UNKNOWN;
  const Icon = state.icon;
  return (
    <div className="min-w-[155px]">
      <div className={`inline-flex items-center gap-1.5 font-semibold ${state.className}`}><Icon size={15} aria-hidden="true" /> {state.label}</div>
      <div className="text-xs text-ink-tertiary mt-1">{EVALUATION_STATE[mildew.evaluation_status] || "Estado de evaluación desconocido"}</div>
    </div>
  );
}

function FieldSummary({ field, onClear }) {
  const state = MILDEW_STATE[field.mildew_state] || MILDEW_STATE.UNKNOWN;
  const Icon = state.icon;
  return (
    <Panel className="p-4 mb-4" testId="sensors-field-summary">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-tertiary">Campo seleccionado</div>
          <h2 className="font-heading font-bold text-lg mt-1">{field.field_name}</h2>
          <div className="text-xs text-ink-tertiary mt-1">{field.field_id} · {field.device_counts?.total || 0} dispositivo{field.device_counts?.total === 1 ? "" : "s"}</div>
        </div>
        <button onClick={onClear} className="text-sm font-semibold text-brand">Ver todos los dispositivos</button>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm">
        <span className={`inline-flex items-center gap-1.5 font-semibold ${state.className}`}><Icon size={15} /> {state.label}</span>
        <span className="text-ink-secondary">Cobertura: {field.coverage === "COMPLETE" ? "Completa" : field.coverage === "PARTIAL" ? "Parcial" : "Sin datos"}</span>
        <span className="text-ink-secondary">{field.device_counts?.risk || 0} con riesgo · {field.device_counts?.unknown || 0} sin valoración</span>
      </div>
    </Panel>
  );
}

export default function Sensors() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fieldFilter = searchParams.get("field") || "all";
  const [data, setData] = useState({ fields: [], devices: [] });
  const [selectedFieldRisk, setSelectedFieldRisk] = useState(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    setLoading(true);
    try {
      setError(null);
      if (fieldFilter === "all") {
        setData(await getFieldsWithDevices());
        setSelectedFieldRisk(null);
      } else {
        const [fieldsEnvelope, riskEnvelope] = await Promise.all([getFields(), getFieldMildewRisk(fieldFilter)]);
        const fieldRisk = riskEnvelope.data;
        const fields = fieldsEnvelope.data || [];
        setData({ fields: fields.length ? fields : [{ id: fieldRisk.field.field_id, name: fieldRisk.field.field_name }], devices: fieldRisk.devices || [] });
        setSelectedFieldRisk(fieldRisk);
      }
    } catch (err) {
      setError(err.message || "No se pudieron cargar los dispositivos");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fieldFilter]);

  useEffect(() => {
    void load();
    const timer = window.setInterval(load, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const changeField = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("field");
    else next.set("field", value);
    setSearchParams(next);
  };

  const filtered = useMemo(() => data.devices.filter((device) => {
    if (stateFilter !== "all" && device.connectivity?.effective_state !== stateFilter) return false;
    return true;
  }), [data.devices, stateFilter]);

  const online = data.devices.filter((device) => device.connectivity?.effective_state === "online").length;
  const stale = data.devices.filter((device) => device.connectivity?.effective_state === "stale").length;
  const selectedField = selectedFieldRisk?.field;
  const detailed = fieldFilter !== "all" && Boolean(selectedFieldRisk);

  return (
    <div className="h-full flex flex-col animate-fade-in">
      <PageHeader title="Dispositivos" subtitle="Estado, mediciones y salud de los dispositivos asociados a esta cuenta" testId="sensors-header" actions={
        <button onClick={() => void load()} disabled={refreshing} className="flex items-center gap-2 text-sm font-semibold text-brand disabled:opacity-60" aria-busy={refreshing}><RefreshCw size={15} className={refreshing ? "animate-spin" : ""} /> {refreshing ? "Actualizando…" : "Actualizar"}</button>
      } />

      {selectedField && <FieldSummary field={selectedField} onClear={() => changeField("all")} />}

      <div className="grid grid-cols-3 gap-4 mb-4 shrink-0">
        {[
          { label: "Total", value: data.devices.length, tone: "text-ink" },
          { label: "En línea", value: online, tone: "text-stable" },
          { label: "Sin datos recientes", value: stale, tone: "text-moderate" },
        ].map((item) => <Panel key={item.label} className="p-4"><div className={`font-heading font-extrabold text-3xl ${item.tone}`}>{item.value}</div><div className="text-xs text-ink-secondary mt-1">{item.label}</div></Panel>)}
      </div>

      <Panel className="p-4 mb-4 flex flex-wrap items-center gap-3" testId="sensors-filters">
        <div className="flex items-center gap-2 text-sm text-ink-secondary"><Filter size={15} /> Filtrar</div>
        <Select value={fieldFilter} onValueChange={changeField}>
          <SelectTrigger className="w-[220px] h-9 bg-surface border-line"><SelectValue placeholder="Campo" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los campos</SelectItem>{data.fields.map((field) => <SelectItem key={field.id} value={field.id}>{field.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-[220px] h-9 bg-surface border-line"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los estados</SelectItem><SelectItem value="online">En línea</SelectItem><SelectItem value="stale">Sin datos recientes</SelectItem><SelectItem value="offline">Sin conexión</SelectItem><SelectItem value="unknown">Desconocido</SelectItem></SelectContent>
        </Select>
        <span className="ml-auto text-xs text-ink-tertiary">{filtered.length} dispositivos</span>
      </Panel>

      {!detailed && !loading && !error && <div className="text-xs text-ink-secondary mb-3">Selecciona un campo para consultar las mediciones actuales y el estado de mildiu de cada dispositivo.</div>}

      <Panel className="overflow-hidden flex-1 min-h-0 flex flex-col" testId="sensors-table">
        {loading ? <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={30} /><span className="sr-only">Cargando dispositivos</span></div>
          : error ? <div className="flex-1 flex flex-col items-center justify-center gap-3 text-critical"><CircleAlert size={28} /><span>{error}</span><button onClick={() => void load()} className="underline font-semibold">Reintentar</button></div>
          : filtered.length === 0 ? <div className="flex-1 flex items-center justify-center text-ink-tertiary">No hay dispositivos para mostrar.</div>
          : <div className="overflow-auto tp-scroll flex-1 min-h-0"><table className="w-full text-sm"><thead><tr className="bg-surface-alt text-ink-secondary text-left sticky top-0 z-10">{["Dispositivo", "Mildiu", "Mediciones actuales", "Conectividad", "Última medición", "Batería", "Señal"].map((heading) => <th key={heading} className="px-4 py-3 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">{heading}</th>)}</tr></thead><tbody>
            {filtered.map((device) => {
              const id = deviceId(device);
              const state = stateLabel(device);
              const ConnectivityIcon = state.icon;
              const latest = latestMeasurements(device);
              const observedAt = latest.observed_at || device.latest_measurement_at;
              return <tr key={id} onClick={() => navigate(`/sensores/${id}`)} className="border-t border-line hover:bg-page cursor-pointer transition-colors">
                <td className="px-4 py-3.5"><div className="font-semibold text-ink flex items-center gap-2"><Radio size={14} className="text-brand" /> {id}</div><div className="text-[11px] text-ink-tertiary ml-6">{device.device_model || "Dispositivo Treeplet"}</div></td>
                <td className="px-4 py-3.5"><MildewCell device={device} /></td>
                <td className="px-4 py-3.5"><MeasurementCell device={device} detailed={detailed} /></td>
                <td className="px-4 py-3.5"><span className={`inline-flex items-center gap-1.5 font-medium ${state.className}`}><ConnectivityIcon size={15} aria-hidden="true" />{state.label}</span></td>
                <td className="px-4 py-3.5 text-ink-secondary whitespace-nowrap">{formatTime(observedAt)}</td>
                <td className="px-4 py-3.5 text-ink-secondary">{device.connectivity?.battery_voltage_v != null ? `${device.connectivity.battery_voltage_v.toFixed(2)} V` : "—"}</td>
                <td className="px-4 py-3.5 text-ink-secondary">{device.connectivity?.signal_rssi_dbm != null ? `${device.connectivity.signal_rssi_dbm} dBm` : "—"}</td>
              </tr>;
            })}
          </tbody></table></div>}
      </Panel>
    </div>
  );
}
